import { User } from '@supabase/supabase-js';

// Types to mimic Supabase auth
export type AuthError = {
  message: string;
  status?: number;
};

export type Session = {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: User;
};

// Demo users for testing
const demoUsers = [
  {
    id: '1',
    email: 'demo@cortex-ai.es',
    user_metadata: {
      name: 'Usuario Demo',
      role: 'product-manager',
    },
  },
  {
    id: '2',
    email: 'developer@cortex-ai.es',
    user_metadata: {
      name: 'Desarrollador Demo',
      role: 'developer',
    },
  },
  {
    id: '3',
    email: 'entrepreneur@cortex-ai.es',
    user_metadata: {
      name: 'Emprendedor Demo',
      role: 'entrepreneur',
    },
  },
  {
    id: '4',
    email: 'consultant@cortex-ai.es',
    user_metadata: {
      name: 'Consultor Demo',
      role: 'consultant',
    },
  },
  {
    id: '5',
    email: 'project-manager@cortex-ai.es',
    user_metadata: {
      name: 'Project Manager Demo',
      role: 'project-manager',
    },
  },
  {
    id: '6',
    email: 'tech-entrepreneur@cortex-ai.es',
    user_metadata: {
      name: 'Tech Entrepreneur Demo',
      role: 'tech-entrepreneur',
    },
  },
  {
    id: '7',
    email: 'admin@cortex-ai.es',
    user_metadata: {
      name: 'Administrador',
      role: 'admin',
    },
  },
  {
    id: '8',
    email: 'empresa@cortex-ai.es',
    user_metadata: {
      name: 'Empresa Demo',
      role: 'employer',
    },
  },
  {
    id: '9',
    email: 'azure-admin@cortex-ai.es',
    user_metadata: {
      name: 'Azure Administrator',
      role: 'azure-admin',
    },
  }
];

// In-memory storage for registered users
let registeredUsers = [...demoUsers];

// In-memory storage for current session
let currentSession: Session | null = null;

// Simulate local storage persistence
const loadSessionFromStorage = (): Session | null => {
  try {
    const savedSession = localStorage.getItem('fakeAuthSession');
    return savedSession ? JSON.parse(savedSession) : null;
  } catch (error) {
    console.error("Error loading session from storage:", error);
    return null;
  }
};

const saveSessionToStorage = (session: Session | null): void => {
  try {
    if (session) {
      localStorage.setItem('fakeAuthSession', JSON.stringify(session));
    } else {
      localStorage.removeItem('fakeAuthSession');
    }
  } catch (error) {
    console.error("Error saving session to storage:", error);
  }
};

// Initialize from storage if available
try {
  currentSession = loadSessionFromStorage();
} catch (error) {
  console.error("Error initializing session:", error);
  currentSession = null;
}

// Fake auth functions
export const fakeAuth = {
  // Get current session
  getSession: async (): Promise<{ data: { session: Session | null } }> => {
    return { data: { session: currentSession } };
  },

  // Sign in with email and password
  signInWithPassword: async (credentials: { 
    email: string; 
    password: string 
  }): Promise<{ 
    data: { session: Session | null; user: User | null }; 
    error: AuthError | null 
  }> => {
    // For demo purposes, accept any password for demo users except admin and employer
    const user = registeredUsers.find(u => u.email === credentials.email);
    
    if (user) {
      // Special check for admin and employer users
      if ((user.email === 'admin@cortex-ai.es' && credentials.password !== 'S@lfum@nXL45') ||
          (user.email === 'empresa@cortex-ai.es' && credentials.password !== 'password123')) {
        return {
          data: { session: null, user: null },
          error: { message: 'Invalid login credentials' },
        };
      }
      
      // Create a fake session
      const session: Session = {
        access_token: `fake-token-${Date.now()}`,
        refresh_token: `fake-refresh-${Date.now()}`,
        expires_at: Date.now() + 3600000, // 1 hour from now
        user: user as User,
      };
      
      currentSession = session;
      saveSessionToStorage(session);
      
      return {
        data: { session, user: user as User },
        error: null,
      };
    }
    
    return {
      data: { session: null, user: null },
      error: { message: 'Invalid login credentials' },
    };
  },

  // Sign up with email and password
  signUp: async (credentials: { 
    email: string; 
    password: string; 
    options?: { data?: any } 
  }): Promise<{ 
    data: { session: Session | null; user: User | null }; 
    error: AuthError | null 
  }> => {
    // Check if user already exists
    const existingUser = registeredUsers.find(u => u.email === credentials.email);
    
    if (existingUser) {
      return {
        data: { session: null, user: null },
        error: { message: 'User already registered' },
      };
    }
    
    // Create new user
    const newUser = {
      id: `user-${Date.now()}`,
      email: credentials.email,
      user_metadata: credentials.options?.data || {},
    };
    
    registeredUsers.push(newUser);
    
    // Create a fake session
    const session: Session = {
      access_token: `fake-token-${Date.now()}`,
      refresh_token: `fake-refresh-${Date.now()}`,
      expires_at: Date.now() + 3600000, // 1 hour from now
      user: newUser as User,
    };
    
    currentSession = session;
    saveSessionToStorage(session);
    
    return {
      data: { session, user: newUser as User },
      error: null,
    };
  },

  // Sign out
  signOut: async (): Promise<void> => {
    currentSession = null;
    saveSessionToStorage(null);
  },

  // Auth state change listener
  onAuthStateChange: (callback: (event: string, session: Session | null) => void) => {
    // This is a simplified version that doesn't actually listen for changes
    // In a real implementation, this would set up event listeners
    
    // Immediately call the callback with the current session
    if (currentSession) {
      callback('SIGNED_IN', currentSession);
    } else {
      callback('SIGNED_OUT', null);
    }
    
    // Return a fake subscription object
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            // This would normally clean up event listeners
          },
        },
      },
    };
  },
};